import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@project/components/ui/dialog";
import { Button } from "@project/components/ui/button";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle,
  Sparkles,
  Ticket,
  ClipboardList,
} from "lucide-react";
import { eventCategoryService } from "@project/services/admin/event-category.service";
import type { EventCategory } from "@project/types/event";

export interface AdminEventFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventToEdit?: any | null;
  onSave: (eventData: any) => void;
}

const formatTimeWithSeconds = (timeStr: string) => {
  if (!timeStr) {
    return "00:00:00";
  }
  const parts = timeStr.split(":");
  if (parts.length === 2) {
    return `${timeStr}:00`;
  }
  return timeStr;
};

// Strips seconds (HH:MM:SS -> HH:MM) for HTML time input
const stripSeconds = (timeStr: string) => {
  if (!timeStr) {
    return "";
  }
  const parts = timeStr.split(":");
  if (parts.length >= 2) {
    return `${parts[0]}:${parts[1]}`;
  }
  return timeStr;
};

export function AdminEventFormDialog({
  open,
  onOpenChange,
  eventToEdit,
  onSave,
}: AdminEventFormDialogProps) {
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  const [formData, setFormData] = useState({
    event_title: "",
    category: "",
    status: "Published",
    date: "",
    start_time: "09:00",
    end_time: "17:00",
    venue: "",
    address: "",
    max_seat_capacity: "150",
    short_summary: "",
    description: "",
    start_date: "",
    end_date: "",
    imageFile: null as File | null,
    imageUrl: "",
    // local mocked registration options
    registration_status: "Open",
    collect_email: true,
    collect_phone: true,
    collect_guests: true,
    collect_special_needs: true,
  });

  // Load categories on open
  useEffect(() => {
    if (open) {
      setLoadingCategories(true);
      eventCategoryService
        .listPage({ page: 1, page_size: 100 })
        .then(res => {
          setCategories(res.data || []);
        })
        .catch(err => {
          console.error("Failed to load event categories:", err);
        })
        .finally(() => {
          setLoadingCategories(false);
        });
    }
  }, [open]);

  // Load or reset form data
  useEffect(() => {
    if (eventToEdit) {
      setFormData({
        event_title: eventToEdit.event_title ?? "",
        category: String(eventToEdit.category ?? ""),
        status: eventToEdit.status ?? "Published",
        date: eventToEdit.date ?? "",
        start_time: stripSeconds(eventToEdit.start_time ?? "09:00:00"),
        end_time: stripSeconds(eventToEdit.end_time ?? "17:00:00"),
        venue: eventToEdit.venue ?? "",
        address: eventToEdit.address ?? "",
        max_seat_capacity: String(eventToEdit.max_seat_capacity ?? 150),
        short_summary: eventToEdit.short_summary ?? "",
        description: eventToEdit.description ?? "",
        start_date: eventToEdit.start_date ?? eventToEdit.date ?? "",
        end_date: eventToEdit.end_date ?? eventToEdit.date ?? "",
        imageFile: null,
        imageUrl: eventToEdit.image ?? "",
        registration_status: eventToEdit.registration_status ?? "Open",
        collect_email: eventToEdit.collect_email ?? true,
        collect_phone: eventToEdit.collect_phone ?? true,
        collect_guests: eventToEdit.collect_guests ?? true,
        collect_special_needs: eventToEdit.collect_special_needs ?? true,
      });
    } else {
      const today = new Date().toISOString().split("T")[0];
      setFormData({
        event_title: "",
        category: "",
        status: "Published",
        date: today,
        start_time: "09:00",
        end_time: "17:00",
        venue: "",
        address: "",
        max_seat_capacity: "150",
        short_summary: "",
        description: "",
        start_date: today,
        end_date: today,
        imageFile: null,
        imageUrl: "",
        registration_status: "Open",
        collect_email: true,
        collect_phone: true,
        collect_guests: true,
        collect_special_needs: true,
      });
    }
  }, [eventToEdit, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      event_title: formData.event_title,
      category: formData.category,
      status: formData.status,
      date: formData.date,
      start_time: formatTimeWithSeconds(formData.start_time),
      end_time: formatTimeWithSeconds(formData.end_time),
      venue: formData.venue,
      address: formData.address,
      max_seat_capacity: Number(formData.max_seat_capacity) || 150,
      short_summary: formData.short_summary,
      description: formData.description,
      start_date: formData.start_date ?? formData.date,
      end_date: formData.end_date ?? formData.date,
      image: formData.imageFile ?? formData.imageUrl ?? null,
      // Pass along the local config just in case
      registration_status: formData.registration_status,
      collect_email: formData.collect_email,
      collect_phone: formData.collect_phone,
      collect_guests: formData.collect_guests,
      collect_special_needs: formData.collect_special_needs,
    });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto p-6 sm:rounded-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl font-bold text-foreground">
            <Sparkles className="h-5 w-5 text-primary" />
            {eventToEdit
              ? "Edit Event & Registration Form"
              : "Create New Event"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          {/* Event Title */}
          <div>
            <Label className="text-xs font-semibold">Event Title *</Label>
            <Input
              required
              value={formData.event_title}
              onChange={e =>
                setFormData({ ...formData, event_title: e.target.value })
              }
              placeholder="e.g. Grand Paryushan Pravachan & Anumodna"
              className="mt-1 text-sm font-medium"
            />
          </div>

          {/* Category & Status Grid */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-semibold">Category *</Label>
              <Select
                value={formData.category}
                onValueChange={val =>
                  setFormData({ ...formData, category: val })
                }
              >
                <SelectTrigger className="mt-1 text-xs">
                  <SelectValue
                    placeholder={
                      loadingCategories ? "Loading..." : "Select Category"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={String(cat.id)}>
                      {cat.category_name}
                    </SelectItem>
                  ))}
                  {categories.length === 0 && !loadingCategories && (
                    <SelectItem value="none" disabled>
                      No categories found. Please create one first.
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs font-semibold">Event Status *</Label>
              <Select
                value={formData.status}
                onValueChange={val => setFormData({ ...formData, status: val })}
              >
                <SelectTrigger className="mt-1 text-xs">
                  <SelectValue placeholder="Select Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Published">
                    Published (Live on site)
                  </SelectItem>
                  <SelectItem value="Draft">Draft (Hidden)</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                  <SelectItem value="Cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Date, Start Date & End Date Grid */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="text-xs font-semibold">
                Primary Event Date *
              </Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  required
                  type="date"
                  value={formData.date}
                  onChange={e => {
                    const d = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      date: d,
                      start_date: prev.start_date || d,
                      end_date: prev.end_date || d,
                    }));
                  }}
                  className="pl-9 text-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Start Date *</Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  required
                  type="date"
                  value={formData.start_date}
                  onChange={e =>
                    setFormData({ ...formData, start_date: e.target.value })
                  }
                  className="pl-9 text-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">End Date *</Label>
              <div className="relative mt-1">
                <Calendar className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  required
                  type="date"
                  value={formData.end_date}
                  onChange={e =>
                    setFormData({ ...formData, end_date: e.target.value })
                  }
                  className="pl-9 text-xs"
                />
              </div>
            </div>
          </div>

          {/* Start Time & End Time */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-semibold">Start Time *</Label>
              <div className="relative mt-1">
                <Clock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  required
                  type="time"
                  value={formData.start_time}
                  onChange={e =>
                    setFormData({ ...formData, start_time: e.target.value })
                  }
                  className="pl-9 text-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">End Time *</Label>
              <div className="relative mt-1">
                <Clock className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  required
                  type="time"
                  value={formData.end_time}
                  onChange={e =>
                    setFormData({ ...formData, end_time: e.target.value })
                  }
                  className="pl-9 text-xs"
                />
              </div>
            </div>
          </div>

          {/* Venue & Address */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs font-semibold">Venue Name *</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  required
                  value={formData.venue}
                  onChange={e =>
                    setFormData({ ...formData, venue: e.target.value })
                  }
                  placeholder="e.g. Jivandhara Campus, Bhojan Hall"
                  className="pl-9 text-xs"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs font-semibold">Full Address *</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  required
                  value={formData.address}
                  onChange={e =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  placeholder="e.g. Palitana, Gujarat"
                  className="pl-9 text-xs"
                />
              </div>
            </div>
          </div>

          {/* Capacity & Banner Image Upload */}
          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="text-xs font-semibold">Max Capacity *</Label>
              <div className="relative mt-1">
                <Ticket className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  required
                  type="number"
                  value={formData.max_seat_capacity}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      max_seat_capacity: e.target.value,
                    })
                  }
                  placeholder="150"
                  className="pl-9 text-xs"
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <Label className="text-xs font-semibold">Banner Image</Label>
              <div className="mt-1 flex items-center gap-3">
                {formData.imageUrl && (
                  <img
                    src={formData.imageUrl}
                    alt="Preview"
                    className="h-9 w-14 rounded border border-border object-cover"
                  />
                )}
                <Input
                  type="file"
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files?.[0] ?? null;
                    if (file) {
                      setFormData({
                        ...formData,
                        imageFile: file,
                        imageUrl: URL.createObjectURL(file),
                      });
                    }
                  }}
                  className="hover:file:bg-primary/90 h-9 text-xs file:mr-2 file:rounded file:border-0 file:bg-primary file:px-2 file:py-0.5 file:text-[11px] file:font-semibold file:text-primary-foreground"
                />
              </div>
            </div>
          </div>

          {/* Dynamic Registration Form Settings Box */}
          <div className="border-primary/20 bg-primary/5 space-y-3 rounded-xl border p-4">
            <div className="flex items-center justify-between">
              <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-primary">
                <ClipboardList className="h-4 w-4" />
                Registration Form Settings
              </h4>
              <Select
                value={formData.registration_status}
                onValueChange={val =>
                  setFormData({ ...formData, registration_status: val })
                }
              >
                <SelectTrigger className="h-7 w-32 bg-background text-xs">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Open">Accepting</SelectItem>
                  <SelectItem value="Closed">Registration Closed</SelectItem>
                  <SelectItem value="Full">Housefull</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-2 pt-1 text-xs">
              <label className="flex cursor-pointer items-center gap-2 font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={formData.collect_phone}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      collect_phone: e.target.checked,
                    })
                  }
                  className="h-3.5 w-3.5 rounded text-primary focus:ring-primary"
                />
                Collect Phone Number
              </label>

              <label className="flex cursor-pointer items-center gap-2 font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={formData.collect_email}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      collect_email: e.target.checked,
                    })
                  }
                  className="h-3.5 w-3.5 rounded text-primary focus:ring-primary"
                />
                Collect Email Address
              </label>

              <label className="flex cursor-pointer items-center gap-2 font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={formData.collect_guests}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      collect_guests: e.target.checked,
                    })
                  }
                  className="h-3.5 w-3.5 rounded text-primary focus:ring-primary"
                />
                Allow Guest Count Input
              </label>

              <label className="flex cursor-pointer items-center gap-2 font-medium text-foreground">
                <input
                  type="checkbox"
                  checked={formData.collect_special_needs}
                  onChange={e =>
                    setFormData({
                      ...formData,
                      collect_special_needs: e.target.checked,
                    })
                  }
                  className="h-3.5 w-3.5 rounded text-primary focus:ring-primary"
                />
                Collect Special Needs Notes
              </label>
            </div>
          </div>

          {/* Short Summary */}
          <div>
            <Label className="text-xs font-semibold">
              Short Summary / Tagline
            </Label>
            <Input
              value={formData.short_summary}
              onChange={e =>
                setFormData({ ...formData, short_summary: e.target.value })
              }
              placeholder="Brief 1-2 sentence overview for cards"
              className="mt-1 text-xs"
            />
          </div>

          {/* Full Description */}
          <div>
            <Label className="text-xs font-semibold">
              Detailed Description
            </Label>
            <Textarea
              rows={3}
              value={formData.description}
              onChange={e =>
                setFormData({ ...formData, description: e.target.value })
              }
              placeholder="Provide complete details about event schedule, speakers, guidelines..."
              className="mt-1 text-xs"
            />
          </div>

          <DialogFooter className="border-t border-border pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-primary font-semibold text-primary-foreground"
            >
              <CheckCircle className="mr-1.5 h-4 w-4" />
              {eventToEdit ? "Save Event Changes" : "Publish Event"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
